import { Injectable } from '@angular/core';
import { AppConfig } from '../../app/app.config';
import { SupportContextKey, SupportTrainingMenuItem, SupportVideo, SupportVideoChapter } from '../../models/support/support.models';

@Injectable()
export class SupportVideoCatalogService {
  private readonly defaultVideoAssetPath = 'assets/videos';
  private readonly videoAssetPath = this.getVideoAssetPath();
  private readonly thumbnailAssetPath = `${this.videoAssetPath}/screenshots`;
  private readonly cadelerVideoNumberToBldVideoNumber: {[key: number]: number} = {
    1: 1,
    2: 2,
    3: 3,
    4: 15,
    5: 16,
    6: 17,
    7: 8,
    8: 4,
    9: 5,
    10: 6,
    11: 9,
    12: 12,
    13: 14,
    14: 18,
    15: 10,
    16: 11,
    17: 13,
    18: 7
  };
  private videos: SupportVideo[] = this.buildVideos();
  private trainingItemsByContext: {[key: string]: SupportTrainingMenuItem[]} = this.buildTrainingItemsByContext();
  private allTrainingItems: SupportTrainingMenuItem[] = this.buildAllTrainingItems();

  constructor() {
    this.applyChapters(this.videos);
  }

  getSuggestedTrainingItems(contextKey: SupportContextKey, additionalContextKeys: SupportContextKey[] = []): SupportTrainingMenuItem[] {
    const contextKeys = [contextKey, ...additionalContextKeys];

    return contextKeys
      .reduce((items, key) => items.concat(this.getTrainingItemsForContext(key)), [] as SupportTrainingMenuItem[])
      .filter((item, index, items) => items.findIndex(candidate => candidate.id === item.id) === index);
  }

  getOtherTrainingItems(contextKey: SupportContextKey, additionalContextKeys: SupportContextKey[] = []): SupportTrainingMenuItem[] {
    const suggestedIds = this.getSuggestedTrainingItems(contextKey, additionalContextKeys).map(item => item.id);

    return this.allTrainingItems.filter(item => suggestedIds.indexOf(item.id) === -1);
  }

  getVideoById(videoId: number): SupportVideo {
    return this.videos.find(video => video.id === videoId);
  }

  getVideoPlayerUrl(itemOrVideo: SupportTrainingMenuItem | SupportVideo): string {
    if (!itemOrVideo) {
      return '';
    }

    const appUrl = window.location.href.split('#')[0];
    const item = itemOrVideo as SupportTrainingMenuItem;

    if (item.video) {
      let url = `${appUrl}#/support-video-player/${item.video.id}?start=${item.startTime}`;

      if (item.endTime) {
        url += `&end=${item.endTime}`;
      }

      return url;
    }

    return `${appUrl}#/support-video-player/${(itemOrVideo as SupportVideo).id}`;
  }

  private buildVideos(): SupportVideo[] {
    const videos = [
      this.createVideo(1, 'Cadeler - Ops App Orientation', 'Cadeler - Ops App Orientation.mp4'),
      this.createVideo(2, 'Introduction to adding and editing the Daily Log', 'Video 2_ Introduction to adding and editing the Daily Log.mp4'),
      this.createVideo(3, 'Daily Log Summary Overview', 'Video 3_ Daily Log Summary Overview.mp4'),
      this.createVideo(4, 'Leg Loads', 'Video 4_ Leg Loads.mp4'),
      this.createVideo(5, 'Weather Log', 'Video 5_ Weather Log.mp4'),
      this.createVideo(6, 'DPR Sections', 'Video 6_ DPR Sections.mp4'),
      this.createVideo(7, 'Voyage Log', 'Video 7_ Voyage Log.mp4'),
      this.createVideo(8, 'Edit Project', 'Video 8_ Edit project.mp4'),
      this.createVideo(9, 'Complete Day and DPR Attachments', 'Video 9_ Completing a Day and Generating a DPR.mp4'),
      this.createVideo(10, 'Change Time Zone', 'Video 10_ Change Time Zone.mp4'),
      this.createVideo(11, 'Implementer Mode', 'Video 11_ Implementer Mode.mp4'),
      this.createVideo(12, 'Change History', 'Video 12_ Change History.mp4'),
      this.createVideo(13, 'Minutes of Meeting', 'Video 13_ MoM (Minutes of Meeting).mp4'),
      this.createVideo(14, 'Jacking Activities', 'Video 14_ Jacking Activities_ Arrivals, Departures and Field Positioning.mp4'),
      this.createVideo(15, 'Comments and operation editing', 'Video 15_ Additional Daily Log Editing Features.mp4'),
      this.createVideo(16, 'Adding Rig Move operations', 'Video 16_ Adding Rig Move Operations to the Daily Log.mp4'),
      this.createVideo(17, 'Adding Delays and Interruptions', 'Video 17_ Adding Delays and Interruptions to the Daily Log.mp4'),
      this.createVideo(18, 'On-Site Movements', 'Video 18_ On-Site Movements.mp4')
    ];

    return videos;
  }

  private buildTrainingItemsByContext(): {[key: string]: SupportTrainingMenuItem[]} {
    return {
      [SupportContextKey.DailyLogSummary]: [
        this.createTrainingItem(SupportContextKey.DailyLogSummary, 3, 'Daily Log Summary Tables', '00:21', '01:18')
      ],
      [SupportContextKey.AddToDailyLog]: [
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 2, 'Add contractual operation', '01:35', '00:50'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 2, 'Add concurrent operation', '04:34', '05:59'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 2, 'Removing an operation from the daily log', '05:59', '06:39'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 2, 'Editing an operation', '06:39', '07:45'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 2, 'Operations with steps', '07:46', '10:56'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 5, 'Adding Rig Move operations', '00:26', '05:29'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 6, 'Adding Delays and Interruptions', '00:42', '07:15'),
        this.createTrainingItem(SupportContextKey.AddToDailyLog, 4, 'Adding Comments', '04:04', '04:26')
      ],
      [SupportContextKey.EditOperation]: [
        this.createTrainingItem(SupportContextKey.EditOperation, 4, 'Switching type of operation', '00:59', '01:27'),
        this.createTrainingItem(SupportContextKey.EditOperation, 4, 'Add point of time comments to an operation', '01:28', '02:39'),
        this.createTrainingItem(SupportContextKey.EditOperation, 4, 'Add comments to start / end of steps', '02:40', '04:04'),
        this.createTrainingItem(SupportContextKey.EditOperation, 4, 'Splitting an operation', '04:26', '05:56')
      ],
      [SupportContextKey.LegLoads]: [
        this.createTrainingItem(SupportContextKey.LegLoads, 8, 'Leg Loads', '00:31', '01:15'),
        this.createTrainingItem(SupportContextKey.LegLoads, 8, 'Additional Leg Load Readings', '01:15', '01:45'),
        this.createTrainingItem(SupportContextKey.LegLoads, 8, 'Manage Readings', '01:45', '02:21'),
        this.createTrainingItem(SupportContextKey.LegLoads, 8, 'View Leg Load Graph', '02:21', '02:44'),
        this.createTrainingItem(SupportContextKey.LegLoads, 8, 'View Total Leg Extension', '02:44', '03:00')
      ],
      [SupportContextKey.WeatherLog]: [
        this.createTrainingItem(SupportContextKey.WeatherLog, 9, 'Weather Log daily entries', '02:27', '03:07'),
        this.createTrainingItem(SupportContextKey.WeatherLog, 9, 'Weather Log height readings', '01:30', '02:21'),
        this.createTrainingItem(SupportContextKey.WeatherLog, 9, 'Weather Remarks / Weather Forecast', '03:07', '03:25'),
        this.createTrainingItem(SupportContextKey.WeatherLog, 9, 'Additional Weather Readings', '03:25', '03:59')
      ],
      [SupportContextKey.DprSections]: [
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'HSEQ', '00:45', '02:29'),
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'POB', '02:30', '04:22'),
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'Consumables', '04:24', '06:20'),
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'Jacking Fuel ROB', '06:22', '07:06'),
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'Daily Observations', '07:08', '07:55'),
        this.createTrainingItem(SupportContextKey.DprSections, 10, 'DPR Attachments', '07:55', '08:02')
      ],
      [SupportContextKey.JackingActivities]: [
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Jacking Activities - Departure', '00:39', '05:37'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Jacking Activities - Arrival', '06:59', '08:35'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Aborting a Jacking Activity', '05:37', '05:56'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Upload File', '05:56', '06:21'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Complete a Jacking Activity', '06:14', '06:59'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 13, 'Field Positioning Report', '08:35', '09:25'),
        this.createTrainingItem(SupportContextKey.JackingActivities, 14, 'On-Site Movements', '00:33', '01:24')
      ],
      [SupportContextKey.VoyageLog]: [
        this.createTrainingItem(SupportContextKey.VoyageLog, 18, 'Voyage Log', '00:23', '03:08')
      ],
      [SupportContextKey.MorningMinutes]: [
        this.createTrainingItem(SupportContextKey.MorningMinutes, 17, 'Minutes of Meeting', '00:21', '02:13')
      ],
      [SupportContextKey.EditProject]: [
        this.createTrainingItem(SupportContextKey.EditProject, 7, 'Edit Project Details', '00:23', '01:38'),
        this.createTrainingItem(SupportContextKey.EditProject, 7, 'Changing Project', '03:06', '04:32'),
        this.createTrainingItem(SupportContextKey.EditProject, 7, 'View Project Details', '01:38', '03:06')
      ],
      [SupportContextKey.ChangeTimeZone]: [
        this.createTrainingItem(SupportContextKey.ChangeTimeZone, 15, 'Change Time Zone', '00:22', '02:26')
      ],
      [SupportContextKey.CompleteDay]: [
        this.createTrainingItem(SupportContextKey.CompleteDay, 11, 'Validation Errors and Warnings', '00:22', '03:10'),
        this.createTrainingItem(SupportContextKey.CompleteDay, 11, 'Preview DPR', '03:10', '04:15'),
        this.createTrainingItem(SupportContextKey.CompleteDay, 11, 'Complete Day', '06:01', '07:04'),
        this.createTrainingItem(SupportContextKey.CompleteDay, 11, 'Sign off Day', '07:04', '08:46')
      ],
      [SupportContextKey.ImplementerMode]: [
        this.createTrainingItem(SupportContextKey.ImplementerMode, 16, 'Activating Implementer Mode', '01:18', '01:54'),
        this.createTrainingItem(SupportContextKey.ImplementerMode, 16, 'Making changes in Implementer Mode', '01:54', '02:47'),
        this.createTrainingItem(SupportContextKey.ImplementerMode, 16, 'Merging changes', '02:47', '04:32')
      ],
      [SupportContextKey.ChangeHistory]: [
        this.createTrainingItem(SupportContextKey.ChangeHistory, 12, 'Change History', '00:28', '01:35')
      ]
    };
  }

  private getTrainingItemsForContext(contextKey: SupportContextKey): SupportTrainingMenuItem[] {
    if (contextKey === SupportContextKey.DailyLog) {
      return this.trainingItemsByContext[SupportContextKey.AddToDailyLog]
        .filter(item => item.title === 'Editing an operation' || item.title === 'Removing an operation from the daily log');
    }

    return this.trainingItemsByContext[contextKey] || [];
  }

  private buildAllTrainingItems(): SupportTrainingMenuItem[] {
    return Object
      .keys(this.trainingItemsByContext)
      .reduce((items, key) => items.concat(this.trainingItemsByContext[key]), [] as SupportTrainingMenuItem[]);
  }

  private applyChapters(videos: SupportVideo[]) {
    const chaptersByVideoId: {[key: number]: SupportVideoChapter[]} = {};

    Object.keys(this.trainingItemsByContext).forEach(key => {
      this.trainingItemsByContext[key].forEach(item => {
        if (!chaptersByVideoId[item.video.id]) {
          chaptersByVideoId[item.video.id] = [];
        }

        if (!chaptersByVideoId[item.video.id].some(chapter => chapter.title === item.title && chapter.startTime === item.startTime)) {
          chaptersByVideoId[item.video.id].push({
            title: item.title,
            startTime: item.startTime,
            endTime: item.endTime
          });
        }
      });
    });

    videos.forEach(video => {
      video.chapters = [
        {
          title: 'Play from start',
          startTime: 0
        },
        ...(chaptersByVideoId[video.id] || []).sort((a, b) => a.startTime - b.startTime)
      ];
    });
  }

  private createVideo(id: number, title: string, fileName: string): SupportVideo {
    return {
      id,
      title,
      fileName,
      url: `${this.videoAssetPath}/${fileName}`,
      chapters: [
        {
          title: 'Play from start',
          startTime: 0
        }
      ]
    };
  }

  private createTrainingItem(
    contextKey: SupportContextKey,
    cadelerVideoId: number,
    title: string,
    startTime: string,
    endTime: string
  ): SupportTrainingMenuItem {
    const videoId = this.cadelerVideoNumberToBldVideoNumber[cadelerVideoId];
    const startSeconds = this.parseTime(startTime);
    const endSeconds = this.parseTime(endTime);

    const id = `${contextKey}-${videoId}-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

    return {
      id,
      title,
      video: this.getVideoById(videoId),
      startTime: Math.min(startSeconds, endSeconds),
      endTime: Math.max(startSeconds, endSeconds),
      thumbnailUrl: `${this.thumbnailAssetPath}/${id}.jpg`
    };
  }

  private parseTime(time: string): number {
    const parts = time.split(':').map(part => Number(part));

    if (parts.length === 3) {
      return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
    }

    return (parts[0] * 60) + parts[1];
  }

  private getVideoAssetPath(): string {
    const configuredPath = AppConfig.settings && AppConfig.settings.supportVideoBaseUrl
      ? AppConfig.settings.supportVideoBaseUrl.trim()
      : '';
    const assetPath = configuredPath || this.defaultVideoAssetPath;

    return this.toBrowserAssetPath(assetPath);
  }

  private toBrowserAssetPath(assetPath: string): string {
    const normalisedPath = (assetPath || '').replace(/\\/g, '/').replace(/\/+$/, '');

    if (/^[a-zA-Z]:\//.test(normalisedPath)) {
      const pathParts = normalisedPath.split('/').filter(part => !!part);

      return pathParts.length
        ? pathParts[pathParts.length - 1]
        : this.defaultVideoAssetPath;
    }

    return normalisedPath || this.defaultVideoAssetPath;
  }
}



// WEBPACK FOOTER //
// ./src/services/support/support-video-catalog.service.ts